/**
 * Gratisversand-Anzeige am rechten Rand.
 *
 * Haelt sich an den Warenkorb: Der Startwert kommt aus Liquid, danach
 * horcht das Element auf «cart:refresh» — dasselbe Ereignis, das das
 * Theme nach jedem Hinzufuegen feuert — und holt sich /cart.js.
 *
 * Bewusst ohne eigenen Zaehler: Wir rechnen nie selbst hoch, sondern
 * fragen immer den echten Warenkorb. Sonst laeuft die Anzeige
 * auseinander, sobald jemand in einem zweiten Tab etwas aendert.
 */
if (!customElements.get('sk-versandbalken')) {
  class SkVersandbalken extends HTMLElement {
    connectedCallback() {
      this.schiene = this.querySelector('.sk-versand__fuellung');
      this.betrag = this.querySelector('.sk-versand__betrag');
      this.grenze = parseInt(this.dataset.grenze, 10) || 0;

      this.zeichne(
        parseInt(this.dataset.summe, 10) || 0,
        parseInt(this.dataset.anzahl, 10) || 0
      );

      this.schalten();

      this.holen = this.holen.bind(this);
      SkVersandbalken.EREIGNISSE.forEach((e) => {
        document.addEventListener(e, this.holen);
        document.documentElement.addEventListener(e, this.holen);
      });
      SkVersandbalken.aufrufeBeobachten();
    }

    disconnectedCallback() {
      SkVersandbalken.EREIGNISSE.forEach((e) => {
        document.removeEventListener(e, this.holen);
        document.documentElement.removeEventListener(e, this.holen);
      });
    }

    /**
     * Auf welche Ereignisse wir horchen.
     *
     * Das Theme hat zwei Formular-Implementierungen mit verschiedenen
     * Meldungen: product.js feuert «cart:refresh» NUR im Fehlerfall,
     * app.js meldet «cart:item-added» nach Erfolg. Darum alle, und
     * zusaetzlich die Beobachtung der Warenkorb-Aufrufe weiter unten.
     */
    static get EREIGNISSE() {
      return ['cart:refresh', 'cart:item-added', 'line-item:change:end', 'cart:updated'];
    }

    /**
     * Letzte Sicherung: jeden Aufruf an /cart/… mitlesen.
     *
     * Egal welcher Codepfad den Artikel hinzufuegt — er geht ueber
     * fetch oder XHR an cart/add, cart/change oder cart/update. Wir
     * reichen alles unveraendert durch und melden danach nur, dass sich
     * etwas geaendert hat.
     */
    static aufrufeBeobachten() {
      if (SkVersandbalken.beobachtet) return;
      SkVersandbalken.beobachtet = true;

      const melden = () => document.dispatchEvent(new CustomEvent('cart:updated'));
      const istWarenkorb = (u) => typeof u === 'string' && /\/cart\/(add|change|update|clear)/.test(u);

      const altesFetch = window.fetch;
      if (altesFetch) {
        window.fetch = function (eingabe, optionen) {
          const url = typeof eingabe === 'string' ? eingabe : (eingabe && eingabe.url) || '';
          const treffer = istWarenkorb(url);
          return altesFetch.apply(this, arguments).then((antwort) => {
            if (treffer) melden();
            return antwort;
          });
        };
      }

      const altesOeffnen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (methode, url) {
        if (istWarenkorb(url)) {
          this.addEventListener('load', melden);
        }
        return altesOeffnen.apply(this, arguments);
      };
    }

    /**
     * Ein- und Ausklappen. Der Zustand haelt fuer den Besuch, damit die
     * Karte nicht auf jeder neuen Seite wieder aufspringt — aber beim
     * naechsten Besuch wieder da ist.
     */
    schalten() {
      const schalter = this.querySelector('.sk-versand__schalter');
      let zu = false;
      try { zu = sessionStorage.getItem('sk-versand-zu') === '1'; } catch (e) {}
      this.classList.toggle('sk-versand--zu', zu);
      if (schalter) schalter.setAttribute('aria-expanded', String(!zu));

      const um = (zu_neu) => {
        this.classList.toggle('sk-versand--zu', zu_neu);
        if (schalter) schalter.setAttribute('aria-expanded', String(!zu_neu));
        try { sessionStorage.setItem('sk-versand-zu', zu_neu ? '1' : '0'); } catch (e) {}
      };

      if (schalter) {
        schalter.addEventListener('click', (e) => {
          e.stopPropagation();
          um(!this.classList.contains('sk-versand--zu'));
        });
      }
      // Eingeklappt oeffnet ein Klick auf den sichtbaren Streifen wieder
      this.addEventListener('click', () => {
        if (this.classList.contains('sk-versand--zu')) um(false);
      });
    }

    holen() {
      // Kleine Verzoegerung: Shopify meldet das Ereignis, bevor der
      // Warenkorb serverseitig fertig geschrieben ist.
      clearTimeout(this.wartet);
      this.wartet = setTimeout(() => {
        fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((k) => this.zeichne(k.total_price, k.item_count))
          .catch(() => {});
      }, 250);
    }

    zeichne(summe, anzahl) {
      const fehlt = Math.max(this.grenze - summe, 0);
      const anteil = this.grenze > 0 ? Math.min(summe / this.grenze, 1) : 0;

      const warVoll = parseInt(this.dataset.anzahl, 10) || 0;
      this.dataset.anzahl = anzahl;
      this.classList.toggle('sk-versand--sichtbar', anzahl > 0);
      // Kommt etwas dazu, klappt sie von selbst wieder auf
      if (anzahl > warVoll && this.classList.contains('sk-versand--zu')) {
        this.classList.remove('sk-versand--zu');
        try { sessionStorage.setItem('sk-versand-zu', '0'); } catch (e) {}
      }
      this.classList.toggle('sk-versand--fertig', fehlt === 0);
      if (this.schiene) this.schiene.style.transform = 'scaleX(' + anteil + ')';

      if (this.betrag && fehlt > 0) {
        this.betrag.textContent = this.waehrung(fehlt);
      }
    }

    /** Nutzt das Format des Shops, damit die Anzeige wie der Rest aussieht. */
    waehrung(rappen) {
      const format = (window.theme && window.theme.settings &&
        window.theme.settings.money_with_currency_format) || 'CHF {{amount}}';
      const betrag = rappen / 100;
      const gerundet = Number.isInteger(betrag)
        ? betrag.toFixed(0)
        : betrag.toFixed(2);
      return format.replace(/\{\{\s*amount[^}]*\}\}/, gerundet);
    }
  }
  customElements.define('sk-versandbalken', SkVersandbalken);
}
